/**
 * Editing the tier stack — adding a band, removing one, and keeping the list
 * something the engine will accept.
 *
 * WHY THESE ARE FUNCTIONS AND NOT INLINE HANDLERS. The tier list has three rules
 * that are easy to state and easy to break:
 *
 *   1. The LAST tier is the catch-all and its floor is `null`. Not zero — zero is
 *      a floor, and it silently drops every credit and adjustment (19 of them in
 *      the reference book) out of the model entirely.
 *   2. Floors DESCEND. The engine buckets by walking the list and taking the
 *      first tier an account clears, so an out-of-order list mis-tiers in
 *      silence rather than erroring.
 *   3. Keys are UNIQUE and, once published, PERMANENT — `staffing_team_slot`
 *      references a tier by key, so renaming a key orphans its seats.
 *
 * Every operation below preserves 1 and 3 by construction. Rule 2 is checked and
 * REPORTED rather than enforced, because forcing it would mean re-sorting rows
 * while someone is typing into them.
 */
import type { StoredTierDto } from '../api';

/** A tier the manager has added but not yet published. */
export const NEW_TIER_LABEL = 'New tier';
export const NEW_NAMED_TIER_LABEL = 'Hand-picked tier';

/**
 * Is this tier filled by hand rather than by spend? Absent means 'band'.
 *
 * One reader, because the absent-means-'band' default has to be interpreted the
 * same way everywhere — a second spelling as `t.selection === 'band'` would
 * treat every pre-existing tier as hand-picked the moment the field is missing.
 */
export function isNamed(tier: StoredTierDto): boolean {
  return tier.selection === 'named';
}

/**
 * A key nothing else is using.
 *
 * `t<n>`, not a slug of the label, and it never changes afterwards. Slots
 * reference a tier BY KEY, so a key that followed the label would orphan every
 * seat on the tier the moment someone corrected a typo in its name. Keys are
 * internal; the label is what anyone reads.
 */
export function nextTierKey(tiers: readonly StoredTierDto[]): string {
  const used = new Set(tiers.map((t) => t.key));
  for (let n = 1; n <= used.size + 1; n += 1) {
    const key = `t${n}`;
    if (!used.has(key)) return key;
  }
  /* c8 ignore next */
  throw new Error('unreachable: n exceeds the number of used keys');
}

const blank = (key: string, minSpendM: number | null): StoredTierDto => ({
  key,
  label: NEW_TIER_LABEL,
  minSpendM,
  // A deliberately small non-zero default. Zero would say this band takes no
  // service time, which computes to zero FTEs and reads as "free".
  hoursPerAccountPerMonth: 1,
  loadedCostPerFte: null,
});

/**
 * Insert a band ABOVE `index`, with a suggested floor between its neighbours.
 *
 * The suggestion matters more than it looks: the whole page is a live preview,
 * so the counts beside the new row update the moment it appears, and a floor
 * that lands somewhere plausible lets a manager see the shape of the band before
 * deciding what it should really be. Inserting at the very top doubles the
 * current highest floor — the "strategic band above Enterprise" case.
 */
export function addTierAbove(
  tiers: readonly StoredTierDto[],
  index: number,
): StoredTierDto[] {
  const below = tiers[index];
  if (!below) return [...tiers];
  const above = index > 0 ? tiers[index - 1] : undefined;
  const belowFloor = below.minSpendM;

  // `above` is never the catch-all: only the LAST tier may carry a null floor,
  // and inserting above it makes `above` the second-to-last at worst.
  let floor: number;
  if (above === undefined) {
    // New top band. Twice the current ceiling, or 1 if the list is only a
    // catch-all and has no numbers in it yet.
    floor = belowFloor === null ? 1 : Math.max(belowFloor * 2, belowFloor + 1);
  } else if (belowFloor === null) {
    // Inserting directly above the catch-all: there is no lower number to
    // bisect towards, so halve the band above. Suggesting the band above's OWN
    // floor would tie with it, and a tie is a violation — floors must strictly
    // descend, or two bands claim the same accounts.
    floor = round2((above.minSpendM ?? 2) / 2);
  } else {
    floor = round2((above.minSpendM ?? belowFloor * 2) / 2 + belowFloor / 2);
  }

  const next = [...tiers];
  next.splice(index, 0, blank(nextTierKey(tiers), floor));
  return next;
}

/**
 * Insert a band BELOW `index`.
 *
 * The interesting case is below the catch-all — the "micro band" idea. The new
 * band becomes the catch-all, which means the OLD catch-all has to acquire a
 * floor it never had. That floor is a real decision (it is the line between
 * "small" and "too small to serve at this rate"), so it is prefilled with a
 * tenth of the band above it and left for the manager to move while watching the
 * counts, rather than being chosen silently.
 */
export function addTierBelow(
  tiers: readonly StoredTierDto[],
  index: number,
): StoredTierDto[] {
  const above = tiers[index];
  if (!above) return [...tiers];
  const key = nextTierKey(tiers);
  const isLast = index === tiers.length - 1;

  if (!isLast) {
    return addTierAbove(tiers, index + 1);
  }

  // Below the catch-all: the roles swap.
  const higher = index > 0 ? tiers[index - 1] : undefined;
  const promotedFloor = round2(
    higher?.minSpendM != null ? higher.minSpendM / 10 : 1,
  );
  const next = tiers.map((t, i) =>
    i === index ? { ...t, minSpendM: promotedFloor } : { ...t },
  );
  next.push(blank(key, null));
  return next;
}

/**
 * Remove a band.
 *
 * Removing the catch-all promotes the one above it, because a tier list with no
 * catch-all is not a tier list — every account below the lowest floor would fall
 * out of the book. Removing the LAST remaining tier is refused for the same
 * reason: the caller gets the list back unchanged.
 *
 * This says nothing about the SEATS on the removed tier. cm-api refuses a
 * publish that would strand them, by name — see `SlotsWouldBeOrphanedError`.
 * Deciding that here would be guessing on the server's behalf.
 */
export function removeTier(
  tiers: readonly StoredTierDto[],
  index: number,
): StoredTierDto[] {
  if (tiers.length <= 1 || !tiers[index]) return [...tiers];
  const next = tiers.filter((_, i) => i !== index).map((t) => ({ ...t }));
  // The catch-all is the last BANDED tier, not the last row: a named tier at the
  // bottom of the display order holds no floor to promote, and giving it one
  // would turn a hand-picked tier into the thing that absorbs every credit and
  // adjustment in the book.
  const lastBand = [...next].reverse().find((t) => !isNamed(t));
  if (lastBand) lastBand.minSpendM = null;
  return next;
}

/**
 * Add a tier whose membership is a DECISION rather than a size.
 *
 * The case this exists for: strategic accounts come in every size, so no
 * threshold describes them, and a band would re-tier them every time bookings
 * moved. A named tier takes NOTHING by spend — the only way in is a per-customer
 * placement — so it goes at the TOP, where the accounts earning the most
 * attention are read first, and it has no floor to order it by anyway.
 */
export function addNamedTier(tiers: readonly StoredTierDto[]): StoredTierDto[] {
  return [
    {
      key: nextTierKey(tiers),
      label: NEW_NAMED_TIER_LABEL,
      // NOT the catch-all. `null` means "everything below" for a band and
      // "nothing at all" for a named tier; `selection` is what tells them apart,
      // and tierProblems() refuses a named tier that claims to be the catch-all.
      minSpendM: null,
      // Higher than any band by default: the reason to hand-pick a tier is
      // usually to spend MORE time per account than its size would buy.
      hoursPerAccountPerMonth: 20,
      loadedCostPerFte: null,
      selection: 'named',
    },
    ...tiers.map((t) => ({ ...t })),
  ];
}

/**
 * Everything wrong with this list, in the words the manager needs.
 *
 * Deliberately a MIRROR of cm-api's `validateStaffingConfig` rather than a
 * client-only nicety: without it, an out-of-order floor makes the engine throw,
 * the page renders a model error, and the only way back is a reload. Reported
 * inline, the same edit is a sentence and a fix.
 */
export function tierProblems(tiers: readonly StoredTierDto[]): string[] {
  const problems: string[] = [];
  if (tiers.length === 0) return ['A model needs at least one tier.'];

  // Bands and named tiers are checked by DIFFERENT rules. A named tier holds no
  // threshold, so it is neither out of order nor a candidate for the catch-all
  // wherever it sits — its position is display order and nothing else.
  const bands = tiers.filter((t) => !isNamed(t));
  if (bands.length === 0) {
    problems.push(
      'Every tier is hand-picked, so there is no catch-all — any customer nobody placed would fall out of the book entirely. Keep at least one tier banded by spend.',
    );
  }

  const seen = new Set<string>();
  tiers.forEach((t, i) => {
    const name = t.label || `Tier ${i + 1}`;
    if (!t.label.trim()) problems.push(`Tier ${i + 1} needs a name.`);
    if (seen.has(t.key))
      problems.push(`Two tiers share the internal key '${t.key}'.`);
    seen.add(t.key);
    if (
      !Number.isFinite(t.hoursPerAccountPerMonth) ||
      t.hoursPerAccountPerMonth < 0
    )
      problems.push(`${name} needs hours per customer of zero or more.`);

    if (isNamed(t)) {
      if (t.minSpendM !== null)
        problems.push(
          `${name} is hand-picked, so it has no spend floor — customers go in by decision, not by size.`,
        );
      return;
    }

    const isCatchAll = t.key === bands[bands.length - 1]?.key;
    if (!isCatchAll && (t.minSpendM === null || !Number.isFinite(t.minSpendM)))
      problems.push(`${name} needs a floor.`);
    if (isCatchAll && t.minSpendM !== null)
      problems.push(
        `${name} is the lowest banded tier, so it catches everything below and cannot have a floor. ` +
          'A floor there would drop credits and adjustments out of the book.',
      );
  });

  for (let i = 1; i < bands.length - 1; i += 1) {
    const prev = bands[i - 1]!;
    const cur = bands[i]!;
    if (
      prev.minSpendM !== null &&
      cur.minSpendM !== null &&
      cur.minSpendM >= prev.minSpendM
    ) {
      problems.push(
        `${cur.label || 'A tier'} ($${cur.minSpendM}M) must sit below ${prev.label} ($${prev.minSpendM}M) — ` +
          'tiers are read top-down and the first one an account clears wins.',
      );
    }
  }

  return problems;
}

const round2 = (n: number) => Math.round(n * 100) / 100;
